import { z } from 'zod';

import { EDIT } from './nodes/edit';
import { EXPERIMENT } from './nodes/experiment';
import { FILTERS } from './nodes/filters';
import { GENERATE } from './nodes/generate';
import { GUARDRAILS } from './nodes/guardrails';
import { OUTPUTS } from './nodes/outputs';
import { STARTERS } from './nodes/starters';
import type { NodeDefinition } from './types';

/** Every node type, in library order. */
export const NODE_TYPES: readonly NodeDefinition[] = [
  ...STARTERS,
  ...FILTERS,
  ...GENERATE,
  ...EDIT,
  ...EXPERIMENT,
  ...GUARDRAILS,
  ...OUTPUTS,
];

const byKey = new Map(NODE_TYPES.map((node) => [`${node.type}@${node.version}`, node]));
const latest = new Map<string, NodeDefinition>();
for (const node of NODE_TYPES) {
  const current = latest.get(node.type);
  if (!current || current.version < node.version) latest.set(node.type, node);
}

/** A node type at a version, or its latest version. */
export function getNodeDefinition(
  type: string,
  version?: number,
): NodeDefinition | undefined {
  return version === undefined ? latest.get(type) : byKey.get(`${type}@${version}`);
}

/** Types whose starters produce items (Schedule only triggers them). */
export function isDataStarter(node: NodeDefinition): boolean {
  return (
    node.category === 'starter' && node.outputs.some((port) => port.type !== 'trigger')
  );
}

/**
 * The config a freshly dropped node starts with: every field's default.
 * Required fields without one (a brief, a persona) stay unset until the user
 * fills them, and validation says so.
 */
export function defaultConfig(node: NodeDefinition): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  if (!(node.config instanceof z.ZodObject)) return config;
  const { shape } = node.config as z.ZodObject<z.ZodRawShape>;
  for (const [key, field] of Object.entries(shape)) {
    const parsed = z.safeParse(field, undefined);
    if (parsed.success && parsed.data !== undefined) config[key] = parsed.data;
  }
  return config;
}
